import React from "react";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from ".././select";

interface TimePicker12HProps {
    value?: string; // Format: "HH:mm" (24-hour)
    onChange?: (value: string) => void;
    disabled?: boolean;
    className?: string;
}

export function TimePicker12H({ value, onChange, disabled, className }: TimePicker12HProps) {
    // Parse the 24-hour time to 12-hour format
    const parseTime = (time24: string) => {
        if (!time24) return { hour: "", minute: "", period: "AM" };
        
        const [hours24, minutes] = time24.split(":").map(Number);
        const period = hours24 >= 12 ? "PM" : "AM";
        let hour12 = hours24 % 12;
        if (hour12 === 0) hour12 = 12;
        
        return {
            hour: hour12.toString(),
            minute: minutes.toString().padStart(2, "0"),
            period
        };
    };

    // Convert 12-hour format to 24-hour format
    const formatTo24Hour = (hour12: string, minute: string, period: string) => {
        let hours24 = parseInt(hour12);
        if (period === "PM" && hours24 !== 12) {
            hours24 += 12;
        } else if (period === "AM" && hours24 === 12) {
            hours24 = 0;
        }
        return `${hours24.toString().padStart(2, "0")}:${minute.padStart(2, "0")}`;
    };

    const { hour, minute, period } = parseTime(value || "");

    const handleHourChange = (newHour: string) => {
        const newTime = formatTo24Hour(newHour, minute || "00", period);
        onChange?.(newTime);
    };

    const handleMinuteChange = (newMinute: string) => {
        const newTime = formatTo24Hour(hour || "12", newMinute, period);
        onChange?.(newTime);
    };

    const handlePeriodChange = (newPeriod: string) => {
        const newTime = formatTo24Hour(hour || "12", minute || "00", newPeriod);
        onChange?.(newTime);
    };

    // Generate hours 1-12
    const hours = Array.from({ length: 12 }, (_, i) => (i + 1).toString());
    
    // Generate minutes 00-59
    const minutes = Array.from({ length: 60 }, (_, i) => i.toString().padStart(2, "0"));

    return (
        <div className={`flex items-center gap-2 ${className || ""}`}>
            <Select value={hour} onValueChange={handleHourChange} disabled={disabled}>
                <SelectTrigger className="w-[70px]">
                    <SelectValue placeholder="Hour" />
                </SelectTrigger>
                <SelectContent className="max-h-[200px] overflow-y-auto">
                    {hours.map((h) => (
                        <SelectItem key={h} value={h}>
                            {h}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>

            <span className="text-gray-500 font-medium">:</span>

            <Select value={minute} onValueChange={handleMinuteChange} disabled={disabled}>
                <SelectTrigger className="w-[70px]">
                    <SelectValue placeholder="Min" />
                </SelectTrigger>
                <SelectContent className="max-h-[200px] overflow-y-auto">
                    {minutes.map((m) => (
                        <SelectItem key={m} value={m}>
                            {m}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>

            <Select value={period} onValueChange={handlePeriodChange} disabled={disabled}>
                <SelectTrigger className="w-[75px]">
                    <SelectValue placeholder="AM/PM" />
                </SelectTrigger>
                <SelectContent>
                    <SelectItem value="AM">AM</SelectItem>
                    <SelectItem value="PM">PM</SelectItem>
                </SelectContent>
            </Select>
        </div>
    );
}

